import { useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { Project, PaginatedResponse, formatMMK, getAttachmentUrl, InvoiceRecord, InvoiceFile } from '@/lib/api'
import DataTable from '@/components/DataTable'
import AdvancedFilters from '@/components/AdvancedFilters'
import FilteredEmptyState from '@/components/FilteredEmptyState'
import SlideOver from '@/components/SlideOver'
import { Plus, Search, ExternalLink, Trash2, FolderOpen, Link as LinkIcon, Paperclip, AlertCircle, Upload, FileText, Image, X, Loader2, Wrench, Clock, DollarSign, MapPin, ArrowUpDown, UserCheck, Users, RefreshCw, Receipt, Edit2, Check, FileDown, Star } from 'lucide-react'
import CommentsSection from '@/components/CommentsSection'
import ProjectDocumentPanel from '@/components/ProjectDocumentPanel'
import { ServiceRequest, UserRole, ADMIN_ROLES, SALES_ROLES } from '@/lib/api'
import { useMyPermissions } from '@/hooks/useRolePermissions'
import AuditInfo from '@/components/AuditInfo'
import { formatDateOnly, formatMyanmarDateTime } from '@/lib/dateUtils'

function DateFilter({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="text-xs font-medium text-slate-600">
      {label}
      <input type="date" value={value} onChange={e => onChange(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
    </label>
  )
}

function formatDuration(createdAt: string, resolvedAt: string | null): string {
  if (!resolvedAt) return '—'
  try {
    const c = new Date(createdAt)
    const r = new Date(resolvedAt)
    const ms = r.getTime() - c.getTime()
    if (ms < 0) return '—'
    const days = Math.floor(ms / 86400000)
    const hours = Math.floor((ms % 86400000) / 3600000)
    const parts: string[] = []
    if (days > 0) parts.push(`${days}d`)
    if (hours > 0) parts.push(`${hours}h`)
    return parts.join(' ') || '< 1h'
  } catch { return '—' }
}

function ServiceHistoryTab({ projectId }: { projectId: number }) {
  const { data: srs, isLoading } = useQuery<ServiceRequest[]>({
    queryKey: ['project-srs', projectId],
    queryFn: () => api.get(`/service-requests/by-project/${projectId}`).then(r => r.data),
  })

  if (isLoading) return <div className="text-sm text-slate-400 text-center py-8">Loading service history...</div>

  const list = srs || []

  const totalBilled = list.filter(s => s.is_billed && s.billing_type === 'Collected').reduce((sum, s) => sum + (Number(s.billing_amount) || 0), 0)
  const resolvedCount = list.filter(s => s.resolved_at).length
  const avgDurationMs = resolvedCount > 0
    ? list.filter(s => s.resolved_at).reduce((sum, s) => {
        const ms = new Date(s.resolved_at!).getTime() - new Date(s.created_at).getTime()
        return sum + (ms > 0 ? ms : 0)
      }, 0) / resolvedCount
    : 0
  const avgDays = Math.floor(avgDurationMs / 86400000)
  const avgHours = Math.floor((avgDurationMs % 86400000) / 3600000)
  const avgStr = resolvedCount > 0 ? (avgDays > 0 ? `${avgDays}d ${avgHours}h` : `${avgHours}h`) : '—'

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-red-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-red-900">{list.length}</div>
          <div className="text-xs text-red-700">Total SRs</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-green-700">{avgStr}</div>
          <div className="text-xs text-green-500">Avg Resolution</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-amber-700">{formatMMK(totalBilled)}</div>
          <div className="text-xs text-amber-500">Total Billed</div>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="text-sm text-slate-400 text-center py-8">No service requests linked to this project yet.</div>
      ) : (
        <div className="space-y-2">
          {list.map(sr => (
            <a key={sr.id} href="/service-requests" className="block bg-white border rounded-lg p-3 hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-red-800 text-sm">{sr.sr_number}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sr.status === 'Open' ? 'bg-red-100 text-red-900' : sr.status === 'In Progress' ? 'bg-purple-100 text-purple-700' : sr.status === 'Resolved' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{sr.status}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span>{sr.category}</span>
                <span className={`px-1.5 py-0.5 rounded ${sr.priority === 'Critical' ? 'bg-red-100 text-red-700' : sr.priority === 'High' ? 'bg-orange-100 text-orange-700' : 'text-slate-500'}`}>{sr.priority}</span>
                {sr.reported_date && <span>{sr.reported_date}</span>}
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                {sr.resolved_at && (
                  <span className="flex items-center gap-1"><Clock size={10} /> {formatDuration(sr.created_at, sr.resolved_at)}</span>
                )}
                {sr.is_billed && (
                  <span className="flex items-center gap-1"><DollarSign size={10} /> {sr.billing_type === 'FOC' ? 'FOC' : formatMMK(Number(sr.billing_amount))}</span>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

interface AttachmentLink {
  label: string
  url: string
}

interface UploadedFile {
  name: string
  url: string
  size: number
  type: string
  storage_path?: string
  uploaded_at?: string
}

interface Attachments {
  onedrive_folder: string
  links: AttachmentLink[]
  files: UploadedFile[]
}

interface SimpleUser {
  id: number
  full_name: string
  role: string
}

interface AssignedTo {
  team_leader: string
  sales_person: string
}

function parseAssignedTo(value: string): AssignedTo {
  if (!value) return { team_leader: '', sales_person: '' }
  try {
    const parsed = JSON.parse(value)
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return {
        team_leader: parsed.team_leader || '',
        sales_person: parsed.sales_person || '',
      }
    }
  } catch {}
  return { team_leader: value, sales_person: '' }
}

function serializeAssignedTo(at: AssignedTo): string {
  return JSON.stringify(at)
}

function displayAssignedTo(value: string): string {
  const at = parseAssignedTo(value)
  const parts = []
  if (at.team_leader) parts.push(at.team_leader)
  if (at.sales_person) parts.push(at.sales_person)
  return parts.join(' / ') || '—'
}

function parseAttachments(documents: string): Attachments {
  if (!documents) return { onedrive_folder: '', links: [], files: [] }
  try {
    const parsed = JSON.parse(documents)
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return {
        onedrive_folder: parsed.onedrive_folder || '',
        links: Array.isArray(parsed.links) ? parsed.links : [],
        files: Array.isArray(parsed.files) ? parsed.files : [],
      }
    }
  } catch {}
  const trimmed = documents.trim()
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return { onedrive_folder: '', links: [{ label: 'Legacy Document', url: trimmed }], files: [] }
  }
  if (trimmed.length > 0) {
    return { onedrive_folder: '', links: [{ label: trimmed, url: '' }], files: [] }
  }
  return { onedrive_folder: '', links: [], files: [] }
}

function serializeAttachments(att: Attachments): string {
  return JSON.stringify(att)
}

function expiryStatus(date: string | null): { label: string; className: string } {
  if (!date) return { label: 'Unknown', className: 'bg-slate-100 text-slate-600' }
  const exp = new Date(`${date.slice(0, 10)}T00:00:00`)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diff = Math.round((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  if (diff < 0) return { label: 'Expired', className: 'bg-red-100 text-red-700' }
  if (diff <= 90) return { label: 'Expiring Soon', className: 'bg-amber-100 text-amber-700' }
  return { label: 'Active', className: 'bg-green-100 text-green-700' }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function FileIcon({ type }: { type: string }) {
  if (type.startsWith('image/')) return <Image size={14} className="text-purple-500" />
  return <FileText size={14} className="text-red-700" />
}

function AttachmentEditor({
  attachments,
  onChange,
  projectId,
  onFileUploaded,
}: {
  attachments: Attachments
  onChange: (a: Attachments) => void
  projectId?: number
  onFileUploaded?: () => void
}) {
  const [newLabel, setNewLabel] = useState('')
  const [newUrl, setNewUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addLink = () => {
    if (!newUrl.trim()) return
    onChange({
      ...attachments,
      links: [...attachments.links, { label: newLabel.trim() || newUrl.trim(), url: newUrl.trim() }],
    })
    setNewLabel('')
    setNewUrl('')
  }

  const removeLink = (idx: number) => {
    onChange({
      ...attachments,
      links: attachments.links.filter((_, i) => i !== idx),
    })
  }

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || !projectId) return
    setUploading(true)
    setUploadError('')
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (file.size > 10 * 1024 * 1024) {
        setUploadError(`${file.name} exceeds 10MB limit`)
        continue
      }
      const formData = new FormData()
      formData.append('file', file)
      try {
        await api.post(`/projects/${projectId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      } catch (err: any) {
        setUploadError(err.response?.data?.detail || `Failed to upload ${file.name}`)
      }
    }
    setUploading(false)
    if (onFileUploaded) onFileUploaded()
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeFile = async (file: UploadedFile) => {
    if (!projectId) return
    try {
      await api.delete(`/projects/${projectId}/attachments/${encodeURIComponent(file.name)}`)
      if (onFileUploaded) onFileUploaded()
    } catch (err: any) {
      setUploadError(err.response?.data?.detail || 'Failed to delete file')
    }
  }

  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
        <Paperclip size={14} />
        Attachments & Links
      </div>

      {projectId && (
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-1">
            <span className="flex items-center gap-1"><Upload size={13} /> Upload Files</span>
          </label>
          <label
            className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-red-400 transition-colors cursor-pointer block"
            onDragOver={e => { e.preventDefault(); e.stopPropagation() }}
            onDrop={e => { e.preventDefault(); e.stopPropagation(); handleFileUpload(e.dataTransfer.files) }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              className="hidden"
              onChange={e => handleFileUpload(e.target.files)}
            />
            {uploading ? (
              <div className="flex items-center justify-center gap-2 text-red-800">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">Uploading...</span>
              </div>
            ) : (
              <div>
                <Upload size={20} className="mx-auto text-slate-400 mb-1" />
                <p className="text-sm text-slate-500">Click or drag files here</p>
                <p className="text-xs text-slate-400">PDF, JPEG, PNG, DOC, XLS — Max 10MB</p>
              </div>
            )}
          </label>
          {uploadError && (
            <p className="text-xs text-red-600 mt-1">{uploadError}</p>
          )}

          {attachments.files.length > 0 && (
            <div className="space-y-1.5 mt-2">
              {attachments.files.map((file, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5 text-sm">
                  <FileIcon type={file.type} />
                  <span className="truncate flex-1 text-slate-700">{file.name}</span>
                  <span className="text-xs text-slate-400 shrink-0">{formatFileSize(file.size)}</span>
                  {file.url && (
                    <button type="button"
                      onClick={() => { const url = getAttachmentUrl(file); if (url) window.open(url, '_blank', 'noopener,noreferrer'); }}
                      className="text-red-800 hover:text-red-900 shrink-0">
                      <ExternalLink size={13} />
                    </button>
                  )}
                  <button type="button" onClick={() => removeFile(file)} className="text-red-400 hover:text-red-600 shrink-0">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!projectId && (
        <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
          Save the project first, then edit it to upload files.
        </p>
      )}

      <div>
        <label className="text-sm font-medium text-slate-700 block mb-1">
          <span className="flex items-center gap-1"><FolderOpen size={13} /> OneDrive / SharePoint Folder</span>
        </label>
        <input
          type="url"
          placeholder="https://onedrive.live.com/..."
          value={attachments.onedrive_folder}
          onChange={e => onChange({ ...attachments, onedrive_folder: e.target.value })}
          className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
        />
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700 block mb-1">
          <span className="flex items-center gap-1"><LinkIcon size={13} /> Document & File Links</span>
        </label>
        {attachments.links.length > 0 && (
          <div className="space-y-1.5 mb-2">
            {attachments.links.map((link, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5 text-sm">
                <Paperclip size={12} className="text-slate-400 shrink-0" />
                <span className="truncate flex-1 text-slate-700">{link.label}</span>
                {link.url && (
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-red-800 hover:text-red-900 shrink-0">
                    <ExternalLink size={13} />
                  </a>
                )}
                <button type="button" onClick={() => removeLink(idx)} className="text-red-400 hover:text-red-600 shrink-0">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <input type="text" placeholder="Label (optional)" value={newLabel} onChange={e => setNewLabel(e.target.value)}
            className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
          <input type="url" placeholder="https://..." value={newUrl} onChange={e => setNewUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLink() } }}
            className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
          <button type="button" onClick={addLink} disabled={!newUrl.trim()}
            className="px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]">
            Add
          </button>
        </div>
      </div>
    </div>
  )
}

function AttachmentViewer({ documents }: { documents: string }) {
  const att = parseAttachments(documents)
  const hasContent = att.onedrive_folder || att.links.length > 0 || att.files.length > 0

  if (!hasContent) return null

  return (
    <div className="space-y-2 pt-3 border-t border-slate-200">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
        <Paperclip size={12} /> Attachments & Links
      </div>

      {att.files.length > 0 && (
        <div className="space-y-1">
          {att.files.map((file, idx) => (
            <button key={idx} type="button"
              onClick={() => { const url = getAttachmentUrl(file); if (url) window.open(url, '_blank', 'noopener,noreferrer'); }}
              className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors w-full text-left">
              <FileIcon type={file.type} />
              <span className="flex-1 truncate">{file.name}</span>
              <span className="text-xs text-slate-400 shrink-0">{formatFileSize(file.size)}</span>
              <ExternalLink size={13} className="text-slate-400" />
            </button>
          ))}
        </div>
      )}

      {att.onedrive_folder && (att.onedrive_folder.startsWith('http://') || att.onedrive_folder.startsWith('https://')) && (
        <a href={att.onedrive_folder} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2 text-sm text-red-900 hover:bg-red-100 transition-colors">
          <FolderOpen size={15} />
          <span className="flex-1">OneDrive Folder</span>
          <ExternalLink size={13} />
        </a>
      )}

      {att.links.length > 0 && (
        <div className="space-y-1">
          {att.links.map((link, idx) => {
            const safeUrl = link.url && (link.url.startsWith('http://') || link.url.startsWith('https://')) ? link.url : ''
            return safeUrl ? (
              <a key={idx} href={safeUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors">
                <Paperclip size={13} className="text-slate-400" />
                <span className="flex-1 truncate">{link.label}</span>
                <ExternalLink size={13} className="text-slate-400" />
              </a>
            ) : (
              <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-700">
                <Paperclip size={13} className="text-slate-400" />
                <span className="flex-1 truncate">{link.label}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Financial Tab ──────────────────────────────────────────────────────────────

interface InvoiceFormState {
  invoice_number: string
  amount: string
  date: string
  notes: string
  pendingFiles: InvoiceFile[]
}

const EMPTY_INVOICE_FORM: InvoiceFormState = {
  invoice_number: '',
  amount: '',
  date: '',
  notes: '',
  pendingFiles: [],
}

async function downloadFinancialFile(sharepointPath: string, fileName: string) {
  try {
    const res = await api.get(`/projects/sharepoint-download?path=${encodeURIComponent(sharepointPath)}`, {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 10000)
  } catch {
    alert('Failed to download file. Please try again.')
  }
}

function FinancialTab({ projectId, userName }: { projectId: number; userName: string }) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<InvoiceFormState>(EMPTY_INVOICE_FORM)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [fileError, setFileError] = useState('')
  const [saveError, setSaveError] = useState('')

  const { data, isLoading } = useQuery<{ records: InvoiceRecord[] }>({
    queryKey: ['project-financial', projectId],
    queryFn: () => api.get(`/projects/${projectId}/financial`).then(r => r.data),
  })

  const records = data?.records || []
  const totalAmount = records.reduce((sum, r) => sum + (Number(r.amount) || 0), 0)

  const saveMutation = useMutation({
    mutationFn: (updated: InvoiceRecord[]) =>
      api.patch(`/projects/${projectId}/financial`, { records: updated }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-financial', projectId] })
      setSaveError('')
      cancelForm()
    },
    onError: (err: any) => {
      setSaveError(err.response?.data?.detail || 'Failed to save')
    },
  })

  const cancelForm = () => {
    setShowAddForm(false)
    setEditingId(null)
    setForm(EMPTY_INVOICE_FORM)
    setFileError('')
    setSaveError('')
  }

  const startEdit = (r: InvoiceRecord) => {
    setEditingId(r.id)
    setShowAddForm(false)
    setForm({
      invoice_number: r.invoice_number,
      amount: String(r.amount),
      date: r.date,
      notes: r.notes || '',
      pendingFiles: r.files || [],
    })
    setSaveError('')
    setFileError('')
  }

  const handleUploadFile = async (files: FileList | null) => {
    if (!files) return
    setFileError('')
    setUploadingFile(true)
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      const fd = new FormData()
      fd.append('file', f)
      try {
        const res = await api.post(`/projects/${projectId}/financial/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        setForm(prev => ({ ...prev, pendingFiles: [...prev.pendingFiles, res.data] }))
      } catch (err: any) {
        setFileError(err.response?.data?.detail || `Failed to upload ${f.name}`)
      }
    }
    setUploadingFile(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeFile = (idx: number) => {
    setForm(prev => ({ ...prev, pendingFiles: prev.pendingFiles.filter((_, i) => i !== idx) }))
  }

  const handleSubmit = () => {
    const amount = parseFloat(form.amount)
    if (!form.invoice_number.trim()) { setSaveError('Invoice number is required'); return }
    if (!form.date) { setSaveError('Date is required'); return }
    if (isNaN(amount) || amount < 0) { setSaveError('Enter a valid amount'); return }
    setSaveError('')

    let updated: InvoiceRecord[]
    if (editingId) {
      updated = records.map(r =>
        r.id === editingId
          ? { ...r, invoice_number: form.invoice_number.trim(), amount, date: form.date, notes: form.notes, files: form.pendingFiles }
          : r
      )
    } else {
      const newRecord: InvoiceRecord = {
        id: `inv_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        invoice_number: form.invoice_number.trim(),
        amount,
        date: form.date,
        notes: form.notes,
        files: form.pendingFiles,
      }
      updated = [...records, newRecord]
    }
    saveMutation.mutate(updated)
  }

  const handleDelete = (id: string) => {
    if (!confirm('Delete this invoice record?')) return
    saveMutation.mutate(records.filter(r => r.id !== id))
  }

  const formatDate = (d: string) => formatMyanmarDateTime(d) || d

  if (isLoading) return <div className="text-sm text-slate-400 text-center py-8">Loading financial records...</div>

  return (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-emerald-600 font-medium uppercase tracking-wide">Total Invoiced</p>
          <p className="text-2xl font-bold text-emerald-800">{formatMMK(totalAmount)}</p>
          <p className="text-xs text-emerald-600">{records.length} invoice{records.length !== 1 ? 's' : ''}</p>
        </div>
        <Receipt size={32} className="text-emerald-300" />
      </div>

      {saveError && (
        <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 px-3 py-2 rounded-lg border border-red-200">
          <AlertCircle size={13} /> {saveError}
        </div>
      )}

      {records.length === 0 && !showAddForm && (
        <div className="text-sm text-slate-400 text-center py-8">No invoices recorded yet.</div>
      )}

      {records.map(r => (
        <div key={r.id} className="border border-slate-200 rounded-xl overflow-hidden">
          {editingId === r.id ? (
            <InvoiceForm
              form={form}
              setForm={setForm}
              onSubmit={handleSubmit}
              onCancel={cancelForm}
              onUploadFile={handleUploadFile}
              onRemoveFile={removeFile}
              fileInputRef={fileInputRef}
              uploadingFile={uploadingFile}
              fileError={fileError}
              saveError={saveError}
              saving={saveMutation.isPending}
              isEdit
              projectId={projectId}
            />
          ) : (
            <div className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 text-sm">{r.invoice_number}</span>
                    <span className="text-xs text-slate-400">{formatDate(r.date)}</span>
                  </div>
                  <p className="text-lg font-bold text-emerald-700 mt-0.5">{formatMMK(Number(r.amount) || 0)}</p>
                  {r.notes && <p className="text-xs text-slate-500 mt-1">{r.notes}</p>}
                  {r.files && r.files.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {r.files.map((f, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => downloadFinancialFile(f.sharepoint_path, f.name)}
                          className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-red-700 w-full text-left"
                        >
                          <FileText size={11} className="text-red-700 shrink-0" />
                          <span className="truncate">{f.name}</span>
                          <ExternalLink size={10} className="shrink-0 text-slate-400" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => startEdit(r)}
                    className="p-1.5 text-slate-400 hover:text-red-700 rounded" title="Edit">
                    <Edit2 size={13} />
                  </button>
                  <button type="button" onClick={() => handleDelete(r.id)}
                    className="p-1.5 text-slate-300 hover:text-red-500 rounded" title="Delete">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {showAddForm && (
        <div className="border border-emerald-200 rounded-xl overflow-hidden bg-emerald-50/40">
          <InvoiceForm
            form={form}
            setForm={setForm}
            onSubmit={handleSubmit}
            onCancel={cancelForm}
            onUploadFile={handleUploadFile}
            onRemoveFile={removeFile}
            fileInputRef={fileInputRef}
            uploadingFile={uploadingFile}
            fileError={fileError}
            saveError={saveError}
            saving={saveMutation.isPending}
            isEdit={false}
            projectId={projectId}
          />
        </div>
      )}

      {!showAddForm && editingId === null && (
        <button
          type="button"
          onClick={() => { setShowAddForm(true); setForm(EMPTY_INVOICE_FORM) }}
          className="w-full py-2.5 text-sm text-emerald-700 border-2 border-dashed border-emerald-300 rounded-xl hover:bg-emerald-50 flex items-center justify-center gap-2 font-medium"
        >
          <Plus size={15} /> Add Invoice
        </button>
      )}
    </div>
  )
}

function InvoiceForm({
  form, setForm, onSubmit, onCancel, onUploadFile, onRemoveFile,
  fileInputRef, uploadingFile, fileError, saveError, saving, isEdit, projectId,
}: {
  form: InvoiceFormState
  setForm: React.Dispatch<React.SetStateAction<InvoiceFormState>>
  onSubmit: () => void
  onCancel: () => void
  onUploadFile: (files: FileList | null) => void
  onRemoveFile: (idx: number) => void
  fileInputRef: React.RefObject<HTMLInputElement>
  uploadingFile: boolean
  fileError: string
  saveError: string
  saving: boolean
  isEdit: boolean
  projectId: number
}) {
  return (
    <div className="p-3 space-y-3">
      <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
        {isEdit ? 'Edit Invoice' : 'New Invoice'}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-0.5">Invoice # *</label>
          <input
            value={form.invoice_number}
            onChange={e => setForm(p => ({ ...p, invoice_number: e.target.value }))}
            placeholder="INV-001"
            className="w-full px-2.5 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-0.5">Date *</label>
          <input
            type="date"
            value={form.date}
            onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
            className="w-full px-2.5 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-0.5">Amount (MMK) *</label>
        <input
          type="number"
          min="0"
          value={form.amount}
          onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
          placeholder="e.g. 5000000"
          className="w-full px-2.5 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-0.5">Notes</label>
        <textarea
          value={form.notes}
          onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
          rows={2}
          placeholder="Optional notes..."
          className="w-full px-2.5 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Attached Files</label>
        {form.pendingFiles.length > 0 && (
          <div className="space-y-1 mb-1.5">
            {form.pendingFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs bg-slate-50 rounded px-2 py-1">
                <FileText size={11} className="text-red-700 shrink-0" />
                <span className="flex-1 truncate">{f.name}</span>
                <button type="button" onClick={() => onRemoveFile(i)} className="text-slate-400 hover:text-red-500">
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
        <label className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer ${uploadingFile ? 'opacity-50 pointer-events-none' : ''}`}>
          {uploadingFile ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
          {uploadingFile ? 'Uploading...' : 'Upload File'}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
            className="hidden"
            onChange={e => onUploadFile(e.target.files)}
            disabled={uploadingFile}
          />
        </label>
        {fileError && <p className="text-xs text-red-600 mt-1">{fileError}</p>}
      </div>

      {saveError && (
        <p className="text-xs text-red-600">{saveError}</p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={saving || uploadingFile}
          className="flex-1 py-1.5 text-sm bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 disabled:opacity-50 font-medium"
        >
          {saving ? 'Saving...' : isEdit ? 'Update' : 'Add Invoice'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="py-1.5 px-4 text-sm bg-slate-100 rounded-lg hover:bg-slate-200"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── End Financial Tab ──────────────────────────────────────────────────────────

const ENGINEERING_USER_ROLES = ['chief_engineer', 'operation_manager', 'engineer', 'technician']
const SALES_USER_ROLES = ['sales_manager', 'asst_sales_manager', 'sales_executive']

const ROLE_SHORT: Record<string, string> = {
  chief_engineer: 'CE', operation_manager: 'OM', engineer: 'Eng', technician: 'Tech',
  procurement: 'Proc', storekeeper: 'SK', sales_manager: 'SM', asst_sales_manager: 'ASM', sales_executive: 'SE',
}

function UserSelect({ label, value, onChange, users }: { label: string; value: string; onChange: (v: string) => void; users: SimpleUser[] }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 block mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
        <option value="">— Select —</option>
        {users.map(u => (
          <option key={u.id} value={u.full_name}>{u.full_name} ({ROLE_SHORT[u.role] || u.role})</option>
        ))}
      </select>
    </div>
  )
}

interface TeamMember {
  id: number
  full_name: string
  role: string
}

function TeamMemberSelect({ selected, onChange, users }: { selected: TeamMember[]; onChange: (v: TeamMember[]) => void; users: SimpleUser[] }) {
  const toggle = (u: SimpleUser) => {
    const exists = selected.find(m => m.id === u.id)
    if (exists) {
      onChange(selected.filter(m => m.id !== u.id))
    } else {
      onChange([...selected, { id: u.id, full_name: u.full_name, role: u.role }])
    }
  }

  return (
    <div>
      <label className="text-sm font-medium text-slate-700 block mb-1">Team Members</label>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map(m => (
            <span key={m.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-800 rounded-full text-xs font-medium">
              {m.full_name}
              <button type="button" onClick={() => onChange(selected.filter(x => x.id !== m.id))} className="text-blue-400 hover:text-blue-700">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="border rounded-lg max-h-36 overflow-y-auto">
        {users.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No engineering users found</div>}
        {users.map(u => (
          <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
            <input type="checkbox" checked={!!selected.find(m => m.id === u.id)} onChange={() => toggle(u)}
              className="rounded border-slate-300 text-red-800" />
            <span className="truncate">{u.full_name}</span>
            <span className="text-xs text-slate-400 ml-auto shrink-0">{ROLE_SHORT[u.role] || u.role}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

interface ContactResult {
  id: number
  client_name: string
  company?: string
  email?: string
  phone?: string
  address?: string
}

function ContactPicker({
  selectedId,
  selectedName,
  onSelect,
}: {
  selectedId: number | null
  selectedName: string
  onSelect: (contact: ContactResult | null) => void
}) {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<ContactResult[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const handleSearch = async (q: string) => {
    setSearch(q)
    if (q.length < 2) { setResults([]); setOpen(false); return }
    setLoading(true)
    try {
      const res = await api.get('/clients/search', { params: { q } })
      setResults(res.data)
      setOpen(true)
    } catch { setResults([]) } finally { setLoading(false) }
  }

  const handleSelect = async (c: ContactResult) => {
    setSearch('')
    setOpen(false)
    try {
      const full = await api.get(`/clients/${c.id}`)
      onSelect({ ...c, ...full.data })
    } catch {
      onSelect(c)
    }
  }

  if (selectedId) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
        <UserCheck size={14} className="text-blue-600 shrink-0" />
        <span className="flex-1 text-blue-900 font-medium truncate">{selectedName}</span>
        <button type="button" onClick={() => onSelect(null)} className="text-blue-400 hover:text-blue-600 shrink-0">
          <X size={14} />
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="relative">
        <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search contacts by name or company..."
          value={search}
          onChange={e => handleSearch(e.target.value)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
        />
        {loading && <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {results.map(c => (
            <button key={c.id} type="button"
              onMouseDown={() => handleSelect(c)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0">
              <div className="font-medium text-slate-900">{c.client_name}</div>
              <div className="text-xs text-slate-500">{[c.company, c.phone].filter(Boolean).join(' · ')}</div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && results.length === 0 && search.length >= 2 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-sm px-3 py-2 text-sm text-slate-500">
          No contacts found
        </div>
      )}
    </div>
  )
}

export default function CustomerDatabase() {
  const queryClient = useQueryClient()
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const userRole = (user.role || '') as UserRole
  const { canDo } = useMyPermissions()
  const canCreate = canDo('project_bank_create')
  const canEdit = canDo('project_bank_edit')
  const canDelete = ADMIN_ROLES.includes(userRole)
  const FINANCIAL_BLOCKED_ROLES: UserRole[] = ['chief_engineer', 'operation_manager', 'engineer', 'technician', 'procurement', 'storekeeper']
  const canViewFinancial = !FINANCIAL_BLOCKED_ROLES.includes(userRole)

  const FINANCIAL_TAB_ALWAYS_ROLES: UserRole[] = ['super_admin', 'admin', 'director', 'finance', 'sales_manager']
  const FINANCIAL_TAB_SALES_ROLES: UserRole[] = ['asst_sales_manager', 'sales_executive']
  const canViewProjectFinancial = (project: Project) => {
    if (FINANCIAL_TAB_ALWAYS_ROLES.includes(userRole)) return true
    if (FINANCIAL_TAB_SALES_ROLES.includes(userRole)) {
      const assigned = parseAssignedTo(project.assigned_to)
      return !!user.full_name && user.full_name === assigned.sales_person
    }
    return false
  }
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [expiryFilter, setExpiryFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [vipFilter, setVipFilter] = useState('')
  const [teamLeaderFilter, setTeamLeaderFilter] = useState('')
  const [salesPersonFilter, setSalesPersonFilter] = useState('')
  const [teamMemberFilter, setTeamMemberFilter] = useState('')
  const [installationFrom, setInstallationFrom] = useState('')
  const [installationTo, setInstallationTo] = useState('')
  const [expectedCompletionFrom, setExpectedCompletionFrom] = useState('')
  const [expectedCompletionTo, setExpectedCompletionTo] = useState('')
  const [completedFrom, setCompletedFrom] = useState('')
  const [completedTo, setCompletedTo] = useState('')
  const [expiryFrom, setExpiryFrom] = useState('')
  const [expiryTo, setExpiryTo] = useState('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [updatedFrom, setUpdatedFrom] = useState('')
  const [updatedTo, setUpdatedTo] = useState('')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Project | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [form, setForm] = useState<any>({})
  const [attachments, setAttachments] = useState<Attachments>({ onedrive_folder: '', links: [], files: [] })
  const [assignedTo, setAssignedTo] = useState<AssignedTo>({ team_leader: '', sales_person: '' })
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [error, setError] = useState('')
  const [detailTab, setDetailTab] = useState<'details' | 'service-history' | 'project-data' | 'financial'>('details')
  const [searchParams, setSearchParams] = useSearchParams()
  const [backfilling, setBackfilling] = useState(false)
  const [backfillResult, setBackfillResult] = useState<{ linked: number; created: number; skipped: number } | null>(null)

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) {
      api.get(`/projects/${openId}`).then(r => {
        setSelected(r.data)
        setEditMode(false)
        setDetailTab('details')
        setSearchParams({}, { replace: true })
      }).catch(() => setSearchParams({}, { replace: true }))
    }
  }, [])

  const advancedProjectParams = {
    team_leader: teamLeaderFilter || undefined, sales_person: salesPersonFilter || undefined, team_member: teamMemberFilter || undefined,
    installation_from: installationFrom || undefined, installation_to: installationTo || undefined,
    expected_completion_from: expectedCompletionFrom || undefined, expected_completion_to: expectedCompletionTo || undefined,
    completed_from: completedFrom || undefined, completed_to: completedTo || undefined,
    expiry_from: expiryFrom || undefined, expiry_to: expiryTo || undefined,
    created_from: createdFrom || undefined, created_to: createdTo || undefined,
    updated_from: updatedFrom || undefined, updated_to: updatedTo || undefined,
    vip_status: vipFilter || undefined,
  }
  const advancedProjectFilterCount = Object.values(advancedProjectParams).filter(Boolean).length
  const resetAdvancedProjectFilters = () => {
    setTeamLeaderFilter(''); setSalesPersonFilter(''); setTeamMemberFilter(''); setVipFilter('')
    setInstallationFrom(''); setInstallationTo(''); setExpectedCompletionFrom(''); setExpectedCompletionTo('')
    setCompletedFrom(''); setCompletedTo(''); setExpiryFrom(''); setExpiryTo('')
    setCreatedFrom(''); setCreatedTo(''); setUpdatedFrom(''); setUpdatedTo(''); setPage(1)
  }
  const resetAllProjectFilters = () => {
    setSearch(''); setTypeFilter(''); setExpiryFilter(''); setStatusFilter(''); resetAdvancedProjectFilters()
  }
  useEffect(() => {
    setPage(1)
  }, [teamLeaderFilter, salesPersonFilter, teamMemberFilter, installationFrom, installationTo, expectedCompletionFrom, expectedCompletionTo, completedFrom, completedTo, expiryFrom, expiryTo, createdFrom, createdTo, updatedFrom, updatedTo])

  const { data, isLoading } = useQuery<PaginatedResponse<Project>>({
    queryKey: ['projects', search, typeFilter, expiryFilter, statusFilter, advancedProjectParams, sortBy, sortDir, page],
    queryFn: () => api.get('/projects', { params: { search, project_type: typeFilter, expiry_status: expiryFilter === 'Expiring Soon' ? 'Expiring' : expiryFilter, status: statusFilter, ...advancedProjectParams, sort_by: sortBy, sort_dir: sortDir, page, per_page: 25 } }).then(r => r.data),
  })

  // Sync selected project when query data updates (e.g., after document upload/delete)
  useEffect(() => {
    if (selected && data?.data) {
      const updated = data.data.find(p => p.id === selected.id)
      if (updated) setSelected(updated)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const { data: userList } = useQuery<SimpleUser[]>({
    queryKey: ['users-list'],
    queryFn: () => api.get('/users/list').then(r => r.data),
  })

  const users = userList || []
  const engineeringUsers = users.filter(u => ENGINEERING_USER_ROLES.includes(u.role))
  const salesUsers = users.filter(u => SALES_USER_ROLES.includes(u.role))

  const createProject = useMutation({
    mutationFn: (data: any) => api.post('/projects', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setShowCreate(false)
      setForm({})
      setAttachments({ onedrive_folder: '', links: [], files: [] })
      setAssignedTo({ team_leader: '', sales_person: '' })
      setTeamMembers([])
      setError('')
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || err.message || 'Failed to create project')
    },
  })

  const updateProject = useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/projects/${id}`, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setSelected(res.data)
      setEditMode(false)
      setError('')
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || err.message || 'Failed to update project')
    },
  })

  const deleteProject = useMutation({
    mutationFn: (id: number) => api.delete(`/projects/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setSelected(null)
      setError('')
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || err.message || 'Failed to delete project')
    },
  })

  const openCreate = () => {
    setShowCreate(true)
    setForm({ project_type: 'CLAPJ', status: 'Confirmed' })
    setAttachments({ onedrive_folder: '', links: [], files: [] })
    setAssignedTo({ team_leader: '', sales_person: '' })
    setTeamMembers([])
    setError('')
  }

  const openEdit = () => {
    if (!selected) return
    setEditMode(true)
    setForm(selected)
    setAttachments(parseAttachments(selected.documents))
    setAssignedTo(parseAssignedTo(selected.assigned_to))
    setTeamMembers(Array.isArray(selected.team_members) ? selected.team_members : [])
    setError('')
  }

  const refreshSelected = async () => {
    if (!selected) return
    try {
      const res = await api.get(`/projects/${selected.id}`)
      setSelected(res.data)
      setAttachments(parseAttachments(res.data.documents))
    } catch {}
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const submitData = {
      ...form,
      assigned_to: serializeAssignedTo(assignedTo),
      documents: serializeAttachments(attachments),
      team_members: teamMembers,
    }
    createProject.mutate(submitData)
  }

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selected) return
    setError('')
    const submitData = {
      id: selected.id,
      ...form,
      assigned_to: serializeAssignedTo(assignedTo),
      documents: serializeAttachments(attachments),
      team_members: teamMembers,
    }
    updateProject.mutate(submitData)
  }

  const statusBadge = (s: string) => {
    const map: Record<string, string> = {
      'Confirmed': 'bg-blue-100 text-blue-700',
      'Procurement': 'bg-orange-100 text-orange-700',
      'In-Progress': 'bg-amber-100 text-amber-700',
      'Complete': 'bg-green-100 text-green-700',
    }
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${map[s] || 'bg-slate-100 text-slate-600'}`}>{s || 'Confirmed'}</span>
  }

  const columns = [
    { key: 'code', label: 'Project Code', render: (p: Project) => <span className="font-medium text-slate-900">{p.project_type}-{p.project_code}</span> },
    { key: 'project_type', label: 'Type', render: (p: Project) => (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.project_type === 'CLAPJ' ? 'bg-red-100 text-red-900' : p.project_type === 'ELVPJ' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-700'}`}>{p.project_type}</span>
    )},
    { key: 'client_name', label: 'Client', render: (p: Project) => <span className="inline-flex items-center gap-1.5">{p.client_name}{p.vip_customer && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold"><Star size={10} fill="currentColor" /> VIP</span>}</span> },
    { key: 'vip_customer', label: 'VIP', render: (p: Project) => p.vip_customer
      ? <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold"><Star size={12} fill="currentColor" /> VIP</span>
      : <span className="text-xs text-slate-400">Non-VIP</span> },
    { key: 'status', label: 'Status', render: (p: Project) => statusBadge(p.status) },
    { key: 'date_completed', label: 'Completed', render: (p: Project) => p.date_completed || '—' },
    { key: 'service_expiry_date', label: 'Expiry', render: (p: Project) => p.service_expiry_date || '—' },
    { key: 'expiry', label: 'Warranty', render: (p: Project) => {
      const s = expiryStatus(p.service_expiry_date)
      return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.className}`}>{s.label}</span>
    }},
    { key: 'assigned_to', label: 'Team Leader', render: (p: Project) => {
      const at = parseAssignedTo(p.assigned_to)
      return <span className="text-sm">{at.team_leader || '—'}</span>
    }},
  ]

  const errorBanner = error ? (
    <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">
      <AlertCircle size={16} className="shrink-0" />
      <span>{error}</span>
    </div>
  ) : null

  const selectedAssigned = selected ? parseAssignedTo(selected.assigned_to) : { team_leader: '', sales_person: '' }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-slate-900">Project Bank</h1>
        <div className="flex items-center gap-2">
          {canDelete && (
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  if (!confirm('Sync all project clients to Contacts? This will match by phone number and create new contacts where needed.')) return
                  setBackfilling(true)
                  setBackfillResult(null)
                  try {
                    const res = await api.post('/projects/backfill-contacts')
                    setBackfillResult(res.data)
                    queryClient.invalidateQueries({ queryKey: ['projects'] })
                  } catch (e: any) {
                    alert(e.response?.data?.detail || 'Backfill failed')
                  } finally { setBackfilling(false) }
                }}
                disabled={backfilling}
                className="flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 text-slate-600"
                title="Sync existing project clients to Contacts">
                <RefreshCw size={14} className={backfilling ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Sync Contacts</span>
              </button>
              {backfillResult && (
                <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-lg">
                  ✓ {backfillResult.linked} linked · {backfillResult.created} created · {backfillResult.skipped} skipped
                </span>
              )}
            </div>
          )}
          <button onClick={async () => {
            try {
              const res = await api.get('/export/projects', { params: { search, project_type: typeFilter, status: statusFilter, expiry_status: expiryFilter === 'Expiring Soon' ? 'Expiring' : expiryFilter, ...advancedProjectParams }, responseType: 'blob' })
              const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = 'projects.xlsx'; a.click(); URL.revokeObjectURL(url)
            } catch {}
          }} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600" title="Export to Excel">
            <FileDown size={15} /> <span className="hidden sm:inline">Export</span>
          </button>
          {canCreate && (
            <button onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900">
              <Plus size={16} /> New Project
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-3 border-b border-slate-200 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input type="text" placeholder="Search projects..." value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
          </div>
          <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            <option value="">All Types</option>
            <option>CLAPJ</option><option>FMTPJ</option><option>ELVPJ</option>
          </select>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            <option value="">All Status</option>
            <option>Confirmed</option><option>Procurement</option><option>In-Progress</option><option>Complete</option>
          </select>
          <select value={expiryFilter} onChange={e => { setExpiryFilter(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            <option value="">All Warranty</option>
              <option>Active</option><option>Expiring Soon</option><option>Expired</option><option>Unknown</option>
          </select>
          <div className="flex items-center gap-1">
            <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1) }}
              className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
              <option value="created_at">Sort: Date Added</option>
              <option value="project_code">Sort: Project Code</option>
              <option value="project_type">Sort: Type</option>
              <option value="client_name">Sort: Client Name</option>
              <option value="status">Sort: Status</option>
              <option value="date_completed">Sort: Completed</option>
              <option value="service_expiry_date">Sort: Expiry</option>
            </select>
            <button onClick={() => { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); setPage(1) }}
              className="px-2 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1"
              title={sortDir === 'asc' ? 'Ascending' : 'Descending'}>
              <ArrowUpDown size={14} />
              <span className="text-xs">{sortDir === 'asc' ? 'A→Z' : 'Z→A'}</span>
            </button>
          </div>
            <AdvancedFilters activeCount={advancedProjectFilterCount} onReset={resetAdvancedProjectFilters} onApply={() => setPage(1)}>
              <label className="text-xs font-medium text-slate-600">Team leader
                <select value={teamLeaderFilter} onChange={e => setTeamLeaderFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm">
                  <option value="">All team leaders</option>{engineeringUsers.map(u => <option key={u.id} value={u.full_name}>{u.full_name}</option>)}
                </select>
              </label>
              <label className="text-xs font-medium text-slate-600">Sales person
                <select value={salesPersonFilter} onChange={e => setSalesPersonFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm">
                  <option value="">All sales people</option>{salesUsers.map(u => <option key={u.id} value={u.full_name}>{u.full_name}</option>)}
                </select>
              </label>
              <label className="text-xs font-medium text-slate-600">Team member
                <select value={teamMemberFilter} onChange={e => setTeamMemberFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm">
                  <option value="">All team members</option>{users.map(u => <option key={u.id} value={u.full_name}>{u.full_name}</option>)}
                </select>
              </label>
              <label className="text-xs font-medium text-slate-600">Customer type
                <select value={vipFilter} onChange={e => setVipFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm">
                  <option value="">All customers</option><option value="VIP">VIP customers</option><option value="Non-VIP">Non-VIP customers</option>
                </select>
              </label>
              <DateFilter label="Installation from" value={installationFrom} onChange={setInstallationFrom} />
              <DateFilter label="Installation to" value={installationTo} onChange={setInstallationTo} />
              <DateFilter label="Expected completion from" value={expectedCompletionFrom} onChange={setExpectedCompletionFrom} />
              <DateFilter label="Expected completion to" value={expectedCompletionTo} onChange={setExpectedCompletionTo} />
              <DateFilter label="Completed from" value={completedFrom} onChange={setCompletedFrom} />
              <DateFilter label="Completed to" value={completedTo} onChange={setCompletedTo} />
              <DateFilter label="Service expiry from" value={expiryFrom} onChange={setExpiryFrom} />
              <DateFilter label="Service expiry to" value={expiryTo} onChange={setExpiryTo} />
              <DateFilter label="Created from" value={createdFrom} onChange={setCreatedFrom} />
              <DateFilter label="Created to" value={createdTo} onChange={setCreatedTo} />
              <DateFilter label="Updated from" value={updatedFrom} onChange={setUpdatedFrom} />
              <DateFilter label="Updated to" value={updatedTo} onChange={setUpdatedTo} />
            </AdvancedFilters>
        </div>
        <DataTable columns={columns} data={data?.data || []} total={data?.total || 0}
          page={page} totalPages={data?.total_pages || 1} onPageChange={setPage}
          onRowClick={p => { setSelected(p); setEditMode(false); setDetailTab('details'); setError(''); }} selectedId={selected?.id}
          getRowId={p => p.id} loading={isLoading} />
        <FilteredEmptyState show={!isLoading && (data?.total || 0) === 0 && !!(search || typeFilter || expiryFilter || statusFilter || advancedProjectFilterCount)} onReset={resetAllProjectFilters} />
      </div>

      <SlideOver open={!!selected} onClose={() => { setSelected(null); setEditMode(false); setError('') }} title={selected ? `${selected.project_type}-${selected.project_code}` : ''} wide>
        {selected && !editMode && (
          <div className="space-y-4">
            <div className="flex border-b border-slate-200 mb-2 overflow-x-auto">
              <button onClick={() => setDetailTab('details')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors shrink-0 ${detailTab === 'details' ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                Details
              </button>
              {canViewFinancial && (
                <button onClick={() => setDetailTab('service-history')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 shrink-0 ${detailTab === 'service-history' ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                  <Wrench size={14} /> Service History
                </button>
              )}
              <button onClick={() => setDetailTab('project-data')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 shrink-0 ${detailTab === 'project-data' ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                <FolderOpen size={14} /> Project Data
              </button>
              {canViewProjectFinancial(selected) && (
                <button onClick={() => setDetailTab('financial')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 shrink-0 ${detailTab === 'financial' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                  <Receipt size={14} /> Financial
                </button>
              )}
            </div>

            {detailTab === 'details' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['Project Code', `${selected.project_type}-${selected.project_code}`],
                    ['Phone', selected.phone],
                    ['Team Leader', selectedAssigned.team_leader],
                    ['Sales Person', selectedAssigned.sales_person],
                    ['Install Start', selected.installation_start_date],
                    ['Expected Completion', selected.expected_completion_date],
                    ['Date Completed', selected.date_completed],
                    ['Expiry Date', selected.service_expiry_date],
                  ].map(([l, v]) => (
                    <div key={l as string} className="text-sm"><span className="text-xs text-slate-500 block">{l}</span><span className="font-medium text-slate-900">{v || '—'}</span></div>
                  ))}
                  <div className="text-sm">
                    <span className="text-xs text-slate-500 block">Client</span>
                    {selected.client_id ? (
                      <a
                        href={`/contacts?client_id=${selected.client_id}`}
                        className="font-medium text-red-700 hover:text-red-900 flex items-center gap-1 w-fit">
                        {selected.client_name || '—'} <ExternalLink size={11} /> {selected.vip_customer && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold"><Star size={10} fill="currentColor" /> VIP</span>}
                      </a>
                    ) : (
                      <span className="font-medium text-slate-900 inline-flex items-center gap-1.5">{selected.client_name || '—'}{selected.vip_customer && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold"><Star size={10} fill="currentColor" /> VIP</span>}</span>
                    )}
                  </div>
                  <div className="text-sm">
                    <span className="text-xs text-slate-500 block">VIP Status</span>
                    {selected.vip_customer
                      ? <span className="inline-flex items-center gap-1.5 mt-1 text-amber-800 font-semibold"><Star size={14} fill="currentColor" className="text-amber-500" /> VIP Customer</span>
                      : <span className="text-slate-500">Non-VIP Customer</span>}
                  </div>
                  <div className="text-sm"><span className="text-xs text-slate-500 block">Status</span>{statusBadge(selected.status)}</div>
                </div>
                {Array.isArray(selected.team_members) && selected.team_members.length > 0 && (
                  <div className="text-sm">
                    <span className="text-xs text-slate-500 block mb-1">Team Members</span>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.team_members.map((m: any) => (
                        <span key={m.id} className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-800 rounded-full text-xs font-medium">
                          {m.full_name} <span className="text-blue-400 ml-1">{ROLE_SHORT[m.role] || m.role}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {selected.address && <div className="text-sm"><span className="text-xs text-slate-500 block">Address</span><p className="text-slate-700">{selected.address}</p></div>}
                {selected.description && <div className="text-sm"><span className="text-xs text-slate-500 block">Description</span><p className="text-slate-700">{selected.description}</p></div>}
                {selected.map_link && (
                  <div className="text-sm space-y-2">
                    <span className="text-xs text-slate-500 block">GPS Map Location</span>
                    <a href={selected.map_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-red-700 hover:text-red-900 font-medium">
                      <MapPin size={14} /> Open in Google Maps <ExternalLink size={12} />
                    </a>
                    {selected.latitude != null && selected.longitude != null && (
                      <iframe
                        src={`https://maps.google.com/maps?q=${selected.latitude},${selected.longitude}&z=15&output=embed`}
                        className="w-full h-48 rounded-lg border border-slate-200"
                        loading="lazy"
                        allowFullScreen
                      />
                    )}
                  </div>
                )}

                <AttachmentViewer documents={selected.documents} />

                <AuditInfo
                  createdByName={selected.created_by_name}
                  createdAt={selected.created_at}
                  updatedAt={selected.updated_at}
                />

                <CommentsSection entityType="project" entityId={selected.id} />
              </>
            )}

            {detailTab === 'service-history' && canViewFinancial && (
              <ServiceHistoryTab projectId={selected.id} />
            )}

            {detailTab === 'project-data' && (
              <ProjectDocumentPanel
                projectId={selected.id}
                projectCode={selected.project_code}
                projectType={selected.project_type}
                assignedTo={selected.assigned_to || ''}
                status={selected.status}
                dateCompleted={selected.date_completed}
                userRole={userRole}
                userName={user?.full_name || ''}
                sitePhotos={selected.site_photos || []}
                preInstallDocs={selected.pre_install_docs || []}
                completionDocs={selected.completion_docs || []}
                context="project-bank"
                invalidateQueryKeys={[['projects']]}
              />
            )}

            {detailTab === 'financial' && canViewProjectFinancial(selected) && (
              <FinancialTab projectId={selected.id} userName={user?.full_name || ''} />
            )}

            {(canEdit || canDelete) && (
              <div className="flex gap-2 pt-3 border-t">
                {canEdit && <button onClick={openEdit} className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Edit</button>}
                {canDelete && <button onClick={() => { if (confirm('Delete this project?')) deleteProject.mutate(selected.id) }}
                  className="py-2 px-4 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100">Delete</button>}
              </div>
            )}
            {errorBanner}
          </div>
        )}
        {selected && editMode && (
          <form onSubmit={handleUpdate} className="space-y-3">
            {errorBanner}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Type *</label>
                <select value={form.project_type || ''} onChange={e => setForm({ ...form, project_type: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
                  <option>CLAPJ</option><option>FMTPJ</option><option>ELVPJ</option></select></div>
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Code *</label>
                <input value={form.project_code || ''} onChange={e => setForm({ ...form, project_code: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Status *</label>
              <select value={form.status || 'Confirmed'} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
                <option>Confirmed</option><option>Procurement</option><option>In-Progress</option><option>Complete</option></select></div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Link Contact</label>
              <ContactPicker
                selectedId={form.client_id || null}
                selectedName={form.client_name || ''}
                onSelect={(c) => {
                  if (c) setForm({ ...form, client_id: c.id, client_name: c.client_name, phone: c.phone || form.phone || '', address: c.address || form.address || '' })
                  else setForm({ ...form, client_id: null })
                }}
              />
            </div>
            {[['client_name', 'Client Name *'], ['phone', 'Phone *'], ['address', 'Address *']].map(([k, l]) => (
              <div key={k}><label className="text-sm font-medium text-slate-700 block mb-1">{l}</label>
                <input value={form[k] || ''} onChange={e => setForm({ ...form, [k]: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            ))}
            <div><label className="text-sm font-medium text-slate-700 block mb-1">GPS Map Location</label>
              <input value={form.map_link || ''} onChange={e => setForm({ ...form, map_link: e.target.value })} placeholder="Paste Google Maps link here" className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            <div className="grid grid-cols-2 gap-3">
              <UserSelect label={`Team Leader${(form.status === 'In-Progress' || form.status === 'Complete') ? ' *' : ''}`} value={assignedTo.team_leader} onChange={v => setAssignedTo({ ...assignedTo, team_leader: v })} users={engineeringUsers} />
              <UserSelect label="Sales Person *" value={assignedTo.sales_person} onChange={v => setAssignedTo({ ...assignedTo, sales_person: v })} users={salesUsers} />
            </div>
            <TeamMemberSelect selected={teamMembers} onChange={setTeamMembers} users={engineeringUsers} />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Install Start Date{(form.status === 'In-Progress' || form.status === 'Complete') ? ' *' : ''}</label>
                <input type="date" value={form.installation_start_date || ''} onChange={e => setForm({ ...form, installation_start_date: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Expected Completion{(form.status === 'In-Progress' || form.status === 'Complete') ? ' *' : ''}</label>
                <input type="date" value={form.expected_completion_date || ''} onChange={e => setForm({ ...form, expected_completion_date: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Date Completed{form.status === 'Complete' ? ' *' : ''}</label>
                <input type="date" value={form.date_completed || ''} onChange={e => setForm({ ...form, date_completed: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Expiry Date{form.status === 'Complete' ? ' *' : ''}</label>
                <input type="date" value={form.service_expiry_date || ''} onChange={e => setForm({ ...form, service_expiry_date: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Description</label>
              <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>

            <AttachmentEditor attachments={attachments} onChange={setAttachments} projectId={selected.id} onFileUploaded={refreshSelected} />

            <div className="flex gap-2">
              <button type="submit" disabled={updateProject.isPending} className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
                {updateProject.isPending ? 'Saving...' : 'Save'}
              </button>
              <button type="button" onClick={() => { setEditMode(false); setError('') }} className="py-2 px-4 text-sm bg-slate-100 rounded-lg">Cancel</button>
            </div>
          </form>
        )}
      </SlideOver>

      <SlideOver open={showCreate} onClose={() => { setShowCreate(false); setError('') }} title="New Project">
        <form onSubmit={handleCreate} className="space-y-3">
          {errorBanner}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Type *</label>
              <select value={form.project_type || 'CLAPJ'} onChange={e => setForm({ ...form, project_type: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
                <option>CLAPJ</option><option>FMTPJ</option><option>ELVPJ</option></select></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Code *</label>
              <input value={form.project_code || ''} onChange={e => setForm({ ...form, project_code: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
          </div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Status *</label>
            <select value={form.status || 'Confirmed'} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
              <option>Confirmed</option><option>Procurement</option><option>In-Progress</option><option>Complete</option></select></div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Link Contact</label>
            <ContactPicker
              selectedId={form.client_id || null}
              selectedName={form.client_name || ''}
              onSelect={(c) => {
                if (c) setForm({ ...form, client_id: c.id, client_name: c.client_name, phone: c.phone || form.phone || '', address: c.address || form.address || '' })
                else setForm({ ...form, client_id: null })
              }}
            />
          </div>
          {[['client_name', 'Client Name *'], ['phone', 'Phone *'], ['address', 'Address *']].map(([k, l]) => (
            <div key={k}><label className="text-sm font-medium text-slate-700 block mb-1">{l}</label>
              <input value={form[k] || ''} onChange={e => setForm({ ...form, [k]: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
          ))}
          <div><label className="text-sm font-medium text-slate-700 block mb-1">GPS Map Location</label>
            <input value={form.map_link || ''} onChange={e => setForm({ ...form, map_link: e.target.value })} placeholder="Paste Google Maps link here" className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
          <div className="grid grid-cols-2 gap-3">
            <UserSelect label={`Team Leader${(form.status === 'In-Progress' || form.status === 'Complete') ? ' *' : ''}`} value={assignedTo.team_leader} onChange={v => setAssignedTo({ ...assignedTo, team_leader: v })} users={engineeringUsers} />
            <UserSelect label="Sales Person *" value={assignedTo.sales_person} onChange={v => setAssignedTo({ ...assignedTo, sales_person: v })} users={salesUsers} />
          </div>
          <TeamMemberSelect selected={teamMembers} onChange={setTeamMembers} users={engineeringUsers} />
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Install Start Date{(form.status === 'In-Progress' || form.status === 'Complete') ? ' *' : ''}</label>
              <input type="date" value={form.installation_start_date || ''} onChange={e => setForm({ ...form, installation_start_date: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Expected Completion{(form.status === 'In-Progress' || form.status === 'Complete') ? ' *' : ''}</label>
              <input type="date" value={form.expected_completion_date || ''} onChange={e => setForm({ ...form, expected_completion_date: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Date Completed{form.status === 'Complete' ? ' *' : ''}</label>
              <input type="date" value={form.date_completed || ''} onChange={e => setForm({ ...form, date_completed: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Expiry Date{form.status === 'Complete' ? ' *' : ''}</label>
              <input type="date" value={form.service_expiry_date || ''} onChange={e => setForm({ ...form, service_expiry_date: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
          </div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Description</label>
            <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>

          <AttachmentEditor attachments={attachments} onChange={setAttachments} />

          <button type="submit" disabled={createProject.isPending} className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
            {createProject.isPending ? 'Creating...' : 'Create Project'}
          </button>
        </form>
      </SlideOver>
    </div>
  )
}
